import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShoppingCart, Plus, Minus, Trash2, Package } from 'lucide-react';

const StorePage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProducts();
    loadCartFromStorage();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/store/products');
      setProducts(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  const loadCartFromStorage = () => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    }
  };

  const saveCartToStorage = (newCart) => {
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.product_id === product.id);
    let newCart;
    
    if (existingItem) {
      newCart = cart.map(item =>
        item.product_id === product.id
          ? { ...item, quantity: item.quantity + 1 }
          : item
      );
    } else {
      newCart = [...cart, { product_id: product.id, name: product.name, price: product.price, quantity: 1 }];
    }
    
    setCart(newCart);
    saveCartToStorage(newCart);
    toast.success(`${product.name} added to cart`);
  };

  const updateQuantity = (productId, delta) => {
    const newCart = cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : null;
      }
      return item;
    }).filter(Boolean);
    
    setCart(newCart);
    saveCartToStorage(newCart);
  };

  const removeFromCart = (productId) => {
    const newCart = cart.filter(item => item.product_id !== productId);
    setCart(newCart);
    saveCartToStorage(newCart);
    toast.success('Item removed from cart');
  };

  const handleCheckout = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to checkout');
      navigate('/login');
      return;
    }

    if (cart.length === 0) {
      toast.error('Cart is empty');
      return;
    }

    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await api.post('/store/checkout', {
        items: cart,
        origin_url: originUrl
      });
      window.location.href = response.data.url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(products.map(p => p.category))];
  const filteredProducts = selectedCategory === 'all'
    ? products
    : products.filter(p => p.category === selectedCategory);

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <div className="min-h-screen bg-background py-20 px-6 md:px-12 lg:px-20">
      <div className="container mx-auto max-w-7xl">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-5xl font-heading font-black uppercase tracking-tight mb-2" data-testid="store-title">
              Gym Store
            </h1>
            <p className="text-muted-foreground">Premium fitness equipment, supplements & accessories</p>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard">
              <Button
                variant="outline"
                className="border-primary text-primary hover:bg-primary hover:text-white"
                data-testid="back-dashboard-btn"
              >
                Back to Dashboard
              </Button>
            </Link>
            <Button
              onClick={() => document.getElementById('cart-section').scrollIntoView({ behavior: 'smooth' })}
              className="bg-primary hover:bg-primary/90 relative"
              data-testid="view-cart-btn"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Cart ({cartCount})
              {cartCount > 0 && (
                <Badge className="absolute -top-2 -right-2 bg-secondary text-black">{cartCount}</Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Category Filter */}
        <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="mb-8">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${categories.length}, 1fr)` }}>
            {categories.map(cat => (
              <TabsTrigger key={cat} value={cat} className="capitalize" data-testid={`category-${cat}`}>
                {cat}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-16">
          {filteredProducts.map(product => (
            <Card key={product.id} className="bg-card/50 border-white/10 hover:border-primary/50 transition-all" data-testid="product-card">
              <CardHeader className="p-0">
                <div className="aspect-square overflow-hidden rounded-t-xl">
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-4">
                <CardTitle className="text-lg mb-2" data-testid="product-name">{product.name}</CardTitle>
                <CardDescription className="text-sm mb-3">{product.description}</CardDescription>
                <div className="flex justify-between items-center">
                  <span className="text-2xl font-bold" data-testid="product-price">₦{product.price.toLocaleString()}</span>
                  <Button
                    onClick={() => addToCart(product)}
                    className="bg-secondary hover:bg-secondary/90 text-black"
                    data-testid="add-to-cart-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {product.stock < 10 && (
                  <Badge variant="destructive" className="mt-2">Only {product.stock} left</Badge>
                )}
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Shopping Cart */}
        <div id="cart-section" className="scroll-mt-20">
          <Card className="bg-card/50 border-white/10">
            <CardHeader>
              <CardTitle className="flex items-center font-heading uppercase">
                <ShoppingCart className="w-6 h-6 mr-2" />
                Shopping Cart ({cartCount} items)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map(item => (
                      <div
                        key={item.product_id}
                        className="flex justify-between items-center border-b border-white/10 pb-4"
                        data-testid="cart-item"
                      >
                        <div className="flex-1">
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-sm text-muted-foreground">₦{item.price.toLocaleString()} each</p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, -1)}
                              data-testid="decrease-quantity-btn"
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-mono" data-testid="item-quantity">{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateQuantity(item.product_id, 1)}
                              data-testid="increase-quantity-btn"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>
                          <span className="font-bold w-24 text-right">₦{(item.price * item.quantity).toLocaleString()}</span>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => removeFromCart(item.product_id)}
                            data-testid="remove-item-btn"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-white/10">
                    <span className="text-2xl font-heading font-bold uppercase">Total:</span>
                    <span className="text-3xl font-bold" data-testid="cart-total">₦{cartTotal.toLocaleString()}</span>
                  </div>
                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full mt-6 bg-primary hover:bg-primary/90 h-14 text-lg font-bold uppercase"
                    data-testid="checkout-btn"
                  >
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default StorePage;